import {ZadProductImportPayload, ZadProductImportRow} from '../../../models/zad'

export const ZAD_PRODUCT_IMPORT_ALLOWED_FIELDS = [
  'code',
  'barcode',
  'name',
  'categoryCode',
  'productTypeCode',
  'purchasePrice',
  'salePrice',
  'publisher',
  'brand',
  'description',
] as const

export const ZAD_PRODUCT_IMPORT_STOCK_FIELDS = [
  'stock',
  'quantity',
  'currentStock',
  'initialStock',
  'availableStock',
  'stockMovement',
] as const

export type ZadProductImportParseErrorCode =
  | 'EMPTY_FILE'
  | 'UNSUPPORTED_FILE'
  | 'INVALID_JSON'
  | 'INVALID_ROOT'
  | 'MISSING_PRODUCTS'
  | 'EMPTY_PRODUCTS'
  | 'INVALID_ROW'
  | 'MISSING_REQUIRED_FIELD'
  | 'INVALID_BARCODE'
  | 'UNSUPPORTED_FIELD'
  | 'UNSUPPORTED_STOCK_FIELD'

export type ZadProductImportWarningCode = 'DUPLICATE_CODE' | 'DUPLICATE_BARCODE'

export interface ZadProductImportWarning {
  code: ZadProductImportWarningCode
  value: string
}

export type ZadProductImportParseResult =
  | {
      ok: true
      payload: ZadProductImportPayload
      fileName: string
      warnings: ZadProductImportWarning[]
    }
  | {
      ok: false
      errorCode: ZadProductImportParseErrorCode
      rowIndex?: number
      field?: string
      productCode?: string
    }

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function requiredString(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== ''
}

function salePriceExists(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value === 'string') return value.trim() !== ''
  return false
}

function optionalPrice(value: unknown): number | string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') return value
  return undefined
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

export function sanitizeZadProductImportRow(row: unknown): ZadProductImportRow {
  const source = isPlainObject(row) ? row : {}
  const product: ZadProductImportRow = {
    code: typeof source.code === 'string' ? source.code : '',
    name: typeof source.name === 'string' ? source.name : '',
    categoryCode: typeof source.categoryCode === 'string' ? source.categoryCode : '',
    productTypeCode: typeof source.productTypeCode === 'string' ? source.productTypeCode : '',
    salePrice:
      typeof source.salePrice === 'number' || typeof source.salePrice === 'string'
        ? source.salePrice
        : '',
  }

  if (typeof source.barcode === 'string') {
    product.barcode = source.barcode
  }

  const purchasePrice = optionalPrice(source.purchasePrice)
  if (purchasePrice !== undefined) product.purchasePrice = purchasePrice

  const publisher = optionalString(source.publisher)
  if (publisher !== undefined) product.publisher = publisher

  const brand = optionalString(source.brand)
  if (brand !== undefined) product.brand = brand

  const description = optionalString(source.description)
  if (description !== undefined) product.description = description

  return product
}

export function sanitizeZadProductImportPayload(raw: unknown): ZadProductImportPayload {
  const source = isPlainObject(raw) ? raw : {}
  const products = Array.isArray(source.products) ? source.products.map(sanitizeZadProductImportRow) : []
  return {products}
}

export function detectZadProductImportDuplicates(
  products: ZadProductImportRow[]
): ZadProductImportWarning[] {
  const codes = new Map<string, number>()
  const barcodes = new Map<string, number>()
  const warnings: ZadProductImportWarning[] = []

  products.forEach((product) => {
    const code = typeof product.code === 'string' ? product.code.trim().toUpperCase() : ''
    if (code) {
      codes.set(code, (codes.get(code) || 0) + 1)
    }
    const barcode = typeof product.barcode === 'string' ? product.barcode.trim() : ''
    if (barcode) {
      barcodes.set(barcode, (barcodes.get(barcode) || 0) + 1)
    }
  })

  codes.forEach((count, value) => {
    if (count > 1) warnings.push({code: 'DUPLICATE_CODE', value})
  })
  barcodes.forEach((count, value) => {
    if (count > 1) warnings.push({code: 'DUPLICATE_BARCODE', value})
  })

  return warnings
}

export function getZadProductImportPreviewCounts(payload: ZadProductImportPayload) {
  const products = payload.products || []
  let withBarcode = 0
  products.forEach((product) => {
    if (typeof product.barcode === 'string' && product.barcode.trim() !== '') {
      withBarcode += 1
    }
  })
  return {
    products: products.length,
    withBarcode,
    withoutBarcode: products.length - withBarcode,
  }
}

function isAllowedProductImportField(field: string) {
  return (ZAD_PRODUCT_IMPORT_ALLOWED_FIELDS as readonly string[]).includes(field)
}

export function isZadProductImportStockField(field: string) {
  return (ZAD_PRODUCT_IMPORT_STOCK_FIELDS as readonly string[]).includes(field)
}

export function findUnsupportedZadProductImportField(row: Record<string, unknown>) {
  return Object.keys(row).find((field) => !isAllowedProductImportField(field))
}

function validateProductRow(
  row: unknown,
  options: {requireMapping?: boolean} = {}
):
  | {ok: true}
  | {ok: false; errorCode: ZadProductImportParseErrorCode; field?: string; productCode?: string} {
  const requireMapping = options.requireMapping !== false
  if (!isPlainObject(row)) {
    return {ok: false, errorCode: 'INVALID_ROW'}
  }

  const productCode = typeof row.code === 'string' && row.code.trim() !== '' ? row.code : undefined
  const unsupportedField = findUnsupportedZadProductImportField(row)
  if (unsupportedField) {
    return {
      ok: false,
      errorCode: isZadProductImportStockField(unsupportedField)
        ? 'UNSUPPORTED_STOCK_FIELD'
        : 'UNSUPPORTED_FIELD',
      field: unsupportedField,
      productCode,
    }
  }

  if (!requiredString(row.code)) {
    return {ok: false, errorCode: 'MISSING_REQUIRED_FIELD', field: 'code'}
  }
  if (!requiredString(row.name)) {
    return {ok: false, errorCode: 'MISSING_REQUIRED_FIELD', field: 'name'}
  }
  if (requireMapping && !requiredString(row.categoryCode)) {
    return {ok: false, errorCode: 'MISSING_REQUIRED_FIELD', field: 'categoryCode'}
  }
  if (requireMapping && !requiredString(row.productTypeCode)) {
    return {ok: false, errorCode: 'MISSING_REQUIRED_FIELD', field: 'productTypeCode'}
  }
  if (!salePriceExists(row.salePrice)) {
    return {ok: false, errorCode: 'MISSING_REQUIRED_FIELD', field: 'salePrice'}
  }

  if (row.barcode !== undefined && row.barcode !== null && typeof row.barcode !== 'string') {
    return {ok: false, errorCode: 'INVALID_BARCODE', field: 'barcode'}
  }

  return {ok: true}
}

export function parseZadProductImportJson(
  text: string,
  fileName = '',
  options: {allowMissingMapping?: boolean} = {}
): ZadProductImportParseResult {
  if (text == null || String(text).trim() === '') {
    return {ok: false, errorCode: 'EMPTY_FILE'}
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return {ok: false, errorCode: 'INVALID_JSON'}
  }

  if (!isPlainObject(parsed)) {
    return {ok: false, errorCode: 'INVALID_ROOT'}
  }

  if (parsed.products === undefined) {
    return {ok: false, errorCode: 'MISSING_PRODUCTS'}
  }

  if (!Array.isArray(parsed.products)) {
    return {ok: false, errorCode: 'MISSING_PRODUCTS'}
  }

  if (parsed.products.length === 0) {
    return {ok: false, errorCode: 'EMPTY_PRODUCTS'}
  }

  for (let index = 0; index < parsed.products.length; index += 1) {
    const rowResult = validateProductRow(parsed.products[index], {
      requireMapping: options.allowMissingMapping !== true,
    })
    if (!rowResult.ok) {
      return {
        ok: false,
        errorCode: rowResult.errorCode,
        rowIndex: index,
        field: rowResult.field,
        productCode: rowResult.productCode,
      }
    }
  }

  const payload = sanitizeZadProductImportPayload(parsed)
  return {
    ok: true,
    payload,
    fileName,
    warnings: detectZadProductImportDuplicates(payload.products),
  }
}

export function parseZadProductImportPreviewJson(
  text: string,
  fileName = ''
): ZadProductImportParseResult {
  return parseZadProductImportJson(text, fileName, {allowMissingMapping: true})
}

export function getZadProductImportExampleJson(): string {
  return `{
  "products": [
    {
      "code": "BOOK-001",
      "barcode": "6190000000012",
      "name": "Let's Go 1 - Student Book",
      "categoryCode": "BOOKS",
      "productTypeCode": "ENGLISH_BOOKS",
      "purchasePrice": 12.500,
      "salePrice": 15.000,
      "publisher": "Promoculture",
      "description": ""
    },
    {
      "code": "BOOK-002",
      "barcode": "00123456789",
      "name": "Let's Go 1 - Workbook",
      "categoryCode": "BOOKS",
      "productTypeCode": "ENGLISH_BOOKS",
      "purchasePrice": 8.000,
      "salePrice": 10.500
    }
  ]
}
`
}

export function downloadZadProductImportExample() {
  const blob = new Blob([getZadProductImportExampleJson()], {type: 'application/json'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'zad-products-example.json'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

